import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowLeft } from "lucide-react";
import { useCMS } from "@/hooks/useCMS";
import { useLang } from "@/context/LanguageContext";
import { imgStyle } from "@/lib/imagePos";

export default function BlogArticle() {
  const { slug } = useParams();
  const { data, loading } = useCMS();
  const { t } = useLang();
  const articles = data.articles || [];
  const a = articles.find((x) => x.slug === slug);
  const related = articles.filter((x) => x.slug !== slug).slice(0, 3);

  if (!a) {
    if (loading) return null;
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-8 py-24 text-center" data-testid="blog-article-missing">
        <p className="text-muted-foreground">{t("Article not found.", "Article introuvable.")}</p>
        <Link to="/Blog" className="mt-4 inline-block font-bold text-[#2CACD4] hover:underline cursor-pointer">← {t("Back to blog", "Retour au blogue")}</Link>
      </div>
    );
  }

  return (
    <article data-testid="blog-article">
      <div className="relative h-[42vh] min-h-[320px]">
        <img src={a.cover} alt={a.cover_alt || a.title} className="h-full w-full object-cover" style={imgStyle(a, "cover")} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-3xl px-4 sm:px-8 pb-8 text-white">
            {a.category && <span className="rounded-full px-3 py-1.5 text-xs font-black uppercase shadow" style={{ background: a.accent || "#2CACD4" }}>{a.category}</span>}
            <h1 className="mt-3 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">{a.title}</h1>
            <p className="mt-3 text-white/80 text-sm">{[a.author, a.date].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-8 py-12">
        <Link to="/Blog" className="inline-flex items-center gap-1.5 text-sm font-bold hover:text-[#2CACD4] transition-colors cursor-pointer" data-testid="blog-back"><ArrowLeft size={16} /> {t("All stories", "Toutes les histoires")}</Link>
        <div className="article-content mt-8" data-testid="blog-article-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.body || "") }} />
      </div>

      {related.length > 0 && (
        <section className="bg-muted/40 py-16" data-testid="related-stories">
          <div className="mx-auto max-w-5xl px-4 sm:px-8">
            <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">{t("More stories", "Plus d'histoires")}</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {related.map((r, i) => (
                <Link key={r.id || i} to={`/Blog/${r.slug}`} className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`related-card-${i}`}>
                  <div className="relative h-36 overflow-hidden">
                    <img src={r.cover} alt={r.cover_alt || r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {r.category && <span className="absolute top-3 left-3 rounded-full px-2.5 py-1 text-xs font-black uppercase text-white shadow" style={{ background: r.accent || "#2CACD4" }}>{r.category}</span>}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-black uppercase text-base leading-tight group-hover:text-[#2CACD4] transition-colors">{r.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
